

/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { createContext, forwardRef, useContext } from "react";
import { useTheme } from "@mui/material/styles";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Custom styles for MDBox
import MDBoxRoot from "components/MDBox/MDBoxRoot";
import { v2 } from "styles/tokens/v2";

/**
 * UI v2 flattens the template's "floating" pattern: a coloured box pulled up
 * over its card with a negative top margin and a coloured shadow.
 *
 * - The header strip on most list and record pages becomes a white card
 *   header: flush along the top of its card, dark title, a hairline under it.
 *   Its contents were written for a blue fill, so it provides
 *   V2StripContext and MDTypography / MDButton swap "white" for dark there
 *   (bare icons are handled in theme-v2's `.v2-strip` rules).
 * - The icon tile on statistics cards sits inside its card, no shadow.
 *
 * Classic is untouched.
 */
export const V2StripContext = createContext(false);

/** In a v2 header strip: content should read on white, not on blue. */
export function useOnV2Strip() {
  return useContext(V2StripContext);
}

/**
 * In the v2 page header's actions. A page's dark filled button there is its
 * main action ("Add Case", "Create Invoice"); MDButton draws it brand blue as
 * in the mockups.
 */
export const V2PageHeaderActionsContext = createContext(false);

export function useInV2PageHeaderActions() {
  return useContext(V2PageHeaderActionsContext);
}

export function isStripForV2(rest) {
  return rest.mx !== undefined && rest.width === undefined;
}

export function flattenForV2(rest) {
  const strip = isStripForV2(rest);
  const override = strip
    ? { borderRadius: "10px 10px 0 0", boxShadow: "none", borderBottom: `1px solid ${v2.border}` }
    : { boxShadow: "none" };
  const sx = Array.isArray(rest.sx) ? rest.sx : rest.sx ? [rest.sx] : [];
  return {
    ...rest,
    mt: 0,
    ...(strip ? { mx: 0, className: [rest.className, "v2-strip"].filter(Boolean).join(" ") } : {}),
    sx: [...sx, override],
  };
}

const MDBox = forwardRef(
  ({ variant, bgColor, color, opacity, borderRadius, shadow, coloredShadow, ...rest }, ref) => {
    const theme = useTheme();
    const floating =
      theme?.pearlUi === "v2" && coloredShadow && coloredShadow !== "none" && Number(rest.mt) < 0;
    if (floating && isStripForV2(rest)) {
      const { children, ...props } = flattenForV2(rest);
      return (
        <MDBoxRoot
          {...props}
          ref={ref}
          ownerState={{ variant: "contained", bgColor: "white", color: "dark", opacity, borderRadius, shadow, coloredShadow: "none" }}
        >
          <V2StripContext.Provider value>{children}</V2StripContext.Provider>
        </MDBoxRoot>
      );
    }
    // Record-page sections (renderSection / renderDetailCard on ~60 detail
    // pages: p={3}, borderRadius="lg", sx boxShadow 3) sit flat inside the
    // details card in v2, so their own padding would double the card's.
    const section =
      theme?.pearlUi === "v2" && !floating && borderRadius === "lg" && Number(rest.p) === 3 && rest.sx?.boxShadow === 3;
    const props = floating ? flattenForV2(rest) : section ? { ...rest, p: 0, mb: 4 } : rest;
    return (
      <MDBoxRoot
        {...props}
        ref={ref}
        ownerState={{
          variant,
          bgColor,
          color,
          opacity,
          borderRadius,
          shadow,
          coloredShadow: floating ? "none" : coloredShadow,
        }}
      />
    );
  }
);

// Setting default values for the props of MDBox
MDBox.defaultProps = {
  variant: "contained",
  bgColor: "transparent",
  color: "dark",
  opacity: 1,
  borderRadius: "none",
  shadow: "none",
  coloredShadow: "none",
};

// Typechecking props for the MDBox
MDBox.propTypes = {
  variant: PropTypes.oneOf(["contained", "gradient"]),
  bgColor: PropTypes.string,
  color: PropTypes.string,
  opacity: PropTypes.number,
  borderRadius: PropTypes.string,
  shadow: PropTypes.string,
  coloredShadow: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
    "none",
  ]),
};

export default MDBox;
